import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const navItems = [
  { name: 'Forside', href: '/' },
  { name: 'Hvem er jeg', href: '/hvem-er-jeg' },
  { name: 'Pædagogiske oplæg', href: '/oplaeg' },
  { name: 'Theraplay', href: '/theraplay' },
  { name: 'Marte Meo', href: '/marte-meo' },
  { name: 'Kontakt', href: '/kontakt' },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const showScrolledStyle = isScrolled;

  const navBg = "bg-[#f5f2ec] shadow-sm border-b border-stone-200/50";
  
  const textColor = "text-stone-600 hover:text-stone-900";
  
  const activeTextColor = "text-stone-900";

  return (
    <nav className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      navBg
    )}>
      <div className={cn(
        "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-300",
        showScrolledStyle ? "pt-1 pb-2 md:pt-2 md:pb-5" : "pt-2 pb-4 md:pt-3 md:pb-6"
      )}>
        <div className="flex flex-col items-center">
          {/* Logo Section */}
          <div className={cn(
            "transition-all duration-300",
            showScrolledStyle ? "mb-2 md:mb-5" : "mb-3 md:mb-5"
          )}>
            <Link to="/" className="flex items-center justify-center">
              <img 
                src="https://assets.auxodev.dk/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/337efd14-67c6-460f-a6a7-440b1b976cdb.png"
                alt="Anette Lundager Schädler" 
                className={cn(
                  "w-auto transition-all duration-300 hover:scale-105",
                  showScrolledStyle ? "h-20 md:h-32" : "h-28 md:h-48"
                )}
              />
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-12">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "text-sm font-semibold tracking-widest uppercase transition-colors",
                  location.pathname === item.href ? activeTextColor : textColor
                )}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <div className={cn(
            "md:hidden absolute right-4 transition-all duration-300",
            showScrolledStyle ? "top-6" : "top-8"
          )}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-stone-600 transition-colors"
            >
              {isOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Menu */}
      <div className={cn(
        "fixed inset-0 z-[60] md:hidden transition-opacity duration-300",
        isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      )}>
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
        
        {/* Sidebar */}
        <div className={cn(
          "absolute top-0 right-0 bottom-0 w-[80%] max-w-sm bg-[#f5f2ec] shadow-2xl transition-transform duration-300 ease-in-out transform",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}>
          <div className="flex flex-col h-full">
            <div className="p-6 flex justify-between items-center border-b border-stone-200">
              <Link to="/" onClick={() => setIsOpen(false)}>
                <img 
                  src="https://assets.auxodev.dk/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/337efd14-67c6-460f-a6a7-440b1b976cdb.png" 
                  alt="Anette Lundager Schädler" 
                  className="h-24 w-auto"
                />
              </Link>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-stone-600 hover:text-stone-900"
              >
                <X className="w-8 h-8" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto py-8 px-6">
              <nav className="space-y-6">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "block text-lg font-semibold tracking-widest uppercase transition-colors",
                      location.pathname === item.href
                        ? "text-stone-900"
                        : "text-stone-500 hover:text-stone-900"
                    )}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
            
            <div className="p-6 border-t border-stone-200 bg-stone-100/50">
              <p className="text-xs text-stone-400 uppercase tracking-widest text-center">
                Anette Lundager Schädler
              </p>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

